import React from 'react';
import { AlertTriangle, Info, RefreshCw, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useAppVersionConfigQuery } from '@/client/api/app-version/app-version.query';
import { MaintenanceCard } from './components/MaintenanceCard';
import { PlatformCard } from './components/PlatformCard';

function formatSavedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const AppVersionPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Settings', href: '#' }, { label: 'App Version' }]);
  const { data, isLoading, isError, error, refetch, isFetching } = useAppVersionConfigQuery();

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Smartphone className="h-6 w-6 text-indigo-500" />
            App Version
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            The iOS and Android apps check these settings on launch to decide whether to show
            maintenance, force an update, suggest one, or carry on.
          </p>
        </div>
        {data && (
          <p className="text-sm text-gray-500">
            {data.updated_at ? `Last saved ${formatSavedAt(data.updated_at)}` : 'Never saved'}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-56 w-full rounded-xl" />
          <div className="grid gap-6 xl:grid-cols-2">
            <Skeleton className="h-[640px] w-full rounded-xl" />
            <Skeleton className="h-[640px] w-full rounded-xl" />
          </div>
        </div>
      ) : isError || !data ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-8 text-center">
          <AlertTriangle className="h-6 w-6 text-red-500" />
          <p className="text-sm text-red-700">
            {getApiErrorMessage(error, 'Could not load the app version settings.')}
          </p>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className="mr-2 h-4 w-4" /> Try again
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Grey text in an empty field is the built-in default, and phones get that value
              until you type your own. Clearing a field puts it back to the default.
              {!data.updated_at && ' Nothing has been saved yet.'}
            </span>
          </div>
          <MaintenanceCard maintenance={data.maintenance} />
          <div className="grid items-start gap-6 xl:grid-cols-2">
            <PlatformCard platform="ios" config={data} />
            <PlatformCard platform="android" config={data} />
          </div>
        </>
      )}
    </div>
  );
};

export default AppVersionPage;
